import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, GraduationCap, Loader2, Lock, ChevronRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { SettingsSheet, SheetBlock } from '../settings/sheet';
import ExportReportModal from '../analytics/ExportReportModal';
import RegisterHubModal from '../register/RegisterHubModal';
import { registerAccess } from '../register/registerAccess';
import type { StoreTabId } from '../shared/StoreNavigation';
import { AttentionStrip } from './AttentionStrip';
import { HomeHeader } from './HomeHeader';
import { HomeLoadError, KpisSkeleton, SectionsSkeleton } from './HomeSkeleton';
import {
  CancellationsSection, ChannelsSection, DaysSection, HoursSection, PaymentsSection, ProductsSection, ReportLink, ReportsBlock,
  StaffSection, TodaySection, compareSuffix,
} from './HomeSections';
import { HomeApiError, HomeData, PeriodSel, fetchLegacyAnalytics, periodDates, periodQuery } from './homeApi';
import type { AttentionData } from './homeApi';
import { KpiDelta, KpiItem, KpiStrip } from './KpiStrip';
import { PeriodPicker } from './PeriodPicker';
import { ReportId, ReportPage, isReportLocked } from './ReportPage';
import { deltaTone, prevFromDelta } from './insights';
import {
  addDaysYmd, athensYmd, fmtDM, fmtDMY, fmtDayMonth, fmtEur, fmtLongDate, fmtMoney, fmtNum, fmtPct, fmtTime, fmtWeekday,
  toAthensYmd, ymdToDate,
} from './format';
import { useHomeData } from './useHomeData';
import { useVizTones } from './charts/useVizTones';
import { rich } from './ui';
import './home.css';

/**
 * Αρχική «Ήσυχο ταμπλό» (σχέδιο A, έγκριση ιδιοκτήτη 13/10/2026): ένα φύλλο όπως οι Ρυθμίσεις — κεφαλίδα
 * με περίοδο και «Εξαγωγή», «Προσοχή» μόνο όταν κάτι θέλει ενέργεια, 5 νούμερα με τάση 7 ημερών και
 * ενότητες «συμπέρασμα | γράφημα» με ευρετήριο. Η λωρίδα με τις 15 καρτέλες του παλιού ταμπλό φεύγει:
 * ό,τι έδειχναν γίνεται ενότητα εδώ ή «Περισσότερες αναφορές» (ReportPage).
 * «Σήμερα» = η ανοιχτή περίοδος ταμείου, συγκρίνεται με το χθες ως την ίδια ώρα (συμβόλαιο home_contract).
 * Basic: όλη η Επισκόπηση· κλειδώνουν μόνο οι αναφορές και η Εξαγωγή (ίδιο κλείδωμα Premium με πριν).
 * Κρυφές «Εκπαιδευτικές»: 5 πατήματα στο «Σήμερα» (ως 13/10 στο «Ανανέωση»).
 */

export interface HomePageProps {
  /** Άλλη καρτέλα της διαχείρισης (Εκτυπωτές, Παραστατικά, Ζ και Χ)· χωρίς αυτό οι σύνδεσμοι κρύβονται. */
  onNavigate?: (tab: StoreTabId) => void;
  /** Αλλάζει όταν πατηθεί ξανά η «Αρχική» στο μενού: από μια αναφορά πίσω στην Επισκόπηση (η περίοδος μένει). */
  resetKey?: number;
}

const TRAINING_TAPS = 5;
const TAP_WINDOW_MS = 4000;

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const arrow = (d: number) => (Math.abs(d) < 0.05 ? '→' : d > 0 ? '↑' : '↓');

/** Ως ποια ώρα μετρά το «χθες» στη σύγκριση του σήμερα: compare.until του server, αλλιώς το «τώρα». */
export const compareUntil = (data: HomeData): string | null => data.period.compare?.until || data.today_so_far?.until || null;

/**
 * Η μέρα του «χθες» (για «Χθες, Δευτέρα»): η προτελευταία του by_day — μία ανά περίοδο ταμείου, με την
 * ημερομηνία του end_of_day του καταστήματος. ΟΧΙ το compare.from (μαγαζί που κλείνει βράδυ → μία μέρα πίσω).
 */
export const yesterdayOf = (data: HomeData, todayYmd: string): string =>
  (data.by_day.length >= 2 ? data.by_day[data.by_day.length - 2].date : '') || addDaysYmd(todayYmd, -1);

/** «7–13 Οκτωβρίου», «28 Σεπτεμβρίου – 4 Οκτωβρίου», ίδια μέρα → «Τρίτη 13 Οκτωβρίου». */
export function rangeText(from: string, to: string, lang?: string): string {
  if (from === to) return fmtLongDate(from, lang);
  const a = ymdToDate(from);
  const b = ymdToDate(to);
  if (a.getUTCFullYear() !== b.getUTCFullYear()) return `${fmtDMY(from)} – ${fmtDMY(to)}`;
  if (a.getUTCMonth() === b.getUTCMonth()) return `${a.getUTCDate()}–${fmtDayMonth(to, lang)}`;
  return `${fmtDayMonth(from, lang)} – ${fmtDayMonth(to, lang)}`;
}

/** «ταμείο ανοιχτό από 06:42» ή «… από Δευτέρα 12/10, 06:51» (ξεχασμένο Ζ). */
function openSinceText(iso: string | null, todayYmd: string, lang: string): string | null {
  const time = fmtTime(iso);
  const day = toAthensYmd(iso);
  if (!time || !day) return null;
  return day === todayYmd ? time : `${fmtWeekday(day, lang)} ${fmtDM(day)}, ${time}`;
}

function buildKpis(t: TFn, data: HomeData, attention: AttentionData | null, opts: { dineIn: boolean; todayYmd: string; lang: string }): KpiItem[] {
  const k = data.kpis;
  const key = data.period.key;
  const suffix = compareSuffix(t, key);
  const until = compareUntil(data);
  const sameHour = data.period.compare?.mode === 'same-hour' && until ? t('home:kpi.sameHour', { time: until }) : undefined;
  const pct = (d: number | null, goodUp: boolean): KpiDelta => ({
    text: d === null ? '—' : `${arrow(d)} ${fmtPct(Math.abs(d))}`,
    tone: deltaTone(d, goodUp),
    suffix,
    title: sameHour,
  });
  const spark = (label: string) => t('home:kpi.spark', { label });

  const canc = k.cancellations;
  const prev = prevFromDelta(canc.value, canc.delta_pct);
  const cancDelta: KpiDelta = prev !== null
    ? { text: `${arrow(canc.value - prev)} ${fmtNum(Math.abs(canc.value - prev))}`, tone: deltaTone(canc.value - prev, false), suffix, title: sameHour }
    : pct(canc.delta_pct, false);
  const cancRate = canc.value + k.orders.value > 0 ? (canc.value / (canc.value + k.orders.value)) * 100 : 0;

  const items: KpiItem[] = [
    { id: 'revenue', label: t('home:kpi.revenue'), value: fmtMoney(k.revenue.value), unit: '€', hero: true, delta: pct(k.revenue.delta_pct, true), spark: k.revenue.series7d, sparkLabel: spark(t('home:kpi.revenue')) },
    { id: 'orders', label: t('home:kpi.orders'), value: fmtNum(k.orders.value), delta: pct(k.orders.delta_pct, true), spark: k.orders.series7d, sparkLabel: spark(t('home:kpi.orders')) },
    { id: 'basket', label: t('home:kpi.avgBasket'), value: fmtMoney(k.avg_basket.value), unit: '€', delta: pct(k.avg_basket.delta_pct, true), spark: k.avg_basket.series7d, sparkLabel: spark(t('home:kpi.avgBasket')) },
    { id: 'cancellations', label: t('home:kpi.cancellations'), value: fmtNum(canc.value), unit: fmtPct(cancRate), delta: cancDelta, spark: canc.series7d, sparkLabel: spark(t('home:kpi.cancellations')) },
  ];

  const reg = attention?.register;
  const since = openSinceText(reg?.open_since ?? null, opts.todayYmd, opts.lang);
  if (reg?.forgotten_z) {
    const hours = reg.hours_open ?? (reg.open_since ? (Date.now() - new Date(reg.open_since).getTime()) / 3600000 : null);
    items.push({
      id: 'z',
      label: t('home:kpi.z'),
      value: t('home:kpi.zForgotten'),
      word: true,
      bad: true,
      rows: [
        hours !== null && Number.isFinite(hours) ? rich(t('home:kpi.zOpenHours', { count: Math.round(hours) })) : null,
        since ? t('home:kpi.zSince', { when: since }) : null,
      ].filter(Boolean) as React.ReactNode[],
    });
  } else {
    const p = k.pending;
    const tables = opts.dineIn;
    items.push({
      id: 'pending',
      label: t('home:kpi.pending'),
      value: fmtNum(tables ? p.open_tables : p.pending_orders),
      unit: tables ? t('home:kpi.tables', { count: p.open_tables }) : t('home:kpi.ordersUnit', { count: p.pending_orders }),
      rows: [
        tables
          ? rich(t('home:kpi.unpaid', { amount: fmtEur(p.open_tables_amount) }))
          : rich(t('home:kpi.inProgressAmount', { amount: fmtEur(p.pending_amount) })),
        tables && p.pending_orders > 0 ? rich(t('home:kpi.inProgress', { count: p.pending_orders })) : null,
        since ? rich(t('home:kpi.zOpenSince', { time: since })) : null,
      ].filter(Boolean) as React.ReactNode[],
    });
  }
  return items;
}

const HomePage: React.FC<HomePageProps> = ({ onNavigate, resetKey }) => {
  const { t: tRaw, i18n } = useTranslation(['home', 'analytics', 'settings', 'common']);
  const t = tRaw as unknown as TFn;
  const lang = i18n.language;
  const { store, employee } = useAuth();
  const premium = store?.analytics_tier === 'premium';
  const [sel, setSel] = useState<PeriodSel>({ key: 'today' });
  const [page, setPage] = useState<'home' | ReportId>('home');
  const [training, setTraining] = useState(false);
  const taps = useRef({ n: 0, at: 0 });
  const home = useHomeData(store?.id, sel);
  const [hubOpen, setHubOpen] = useState(false);
  const [exp, setExp] = useState<{ busy: boolean; open: boolean; analytics: unknown; error: boolean }>({ busy: false, open: false, analytics: null, error: false });
  const rootRef = useRef<HTMLDivElement>(null);
  const vizStyle = useVizTones(rootRef, [page, home.data === null]);

  const now = new Date();
  const todayYmd = athensYmd(now);
  const data = home.data;
  const shownData = data && home.current ? data : null;
  const dates = periodDates(sel, shownData, now);
  const access = registerAccess(store, employee);

  const changePeriod = (next: PeriodSel) => {
    // Ίδια περίοδος ξανά (π.χ. «Σήμερα» όταν είναι ήδη επιλεγμένο): ανανέωση.
    if (periodQuery(next) === periodQuery(sel)) home.reload();
    else setSel(next);
  };

  const onTodayTap = useCallback(() => {
    const ms = Date.now();
    const tp = taps.current;
    tp.n = ms - tp.at <= TAP_WINDOW_MS ? tp.n + 1 : 1;
    tp.at = ms;
    if (tp.n >= TRAINING_TAPS) {
      tp.n = 0;
      setTraining(v => !v);
    }
  }, []);

  const openReport = useCallback((id: string) => {
    setPage(id as ReportId);
  }, []);

  const backHome = useCallback(() => {
    setPage('home');
    try { window.scrollTo(0, 0); } catch { /* jsdom */ }
  }, []);

  // «Αρχική» στο μενού ενώ είσαι ήδη εδώ → πίσω στην Επισκόπηση (όχι στο πρώτο render).
  const seenReset = useRef(resetKey);
  useEffect(() => {
    if (resetKey === seenReset.current) return;
    seenReset.current = resetKey;
    backHome();
  }, [resetKey, backHome]);

  const openExport = async () => {
    if (!premium || !store?.id || exp.busy) return;
    setExp(s => ({ ...s, busy: true, error: false }));
    try {
      const analytics = await fetchLegacyAnalytics(store.id, dates.from, dates.to);
      setExp({ busy: false, open: true, analytics, error: false });
    } catch {
      setExp(s => ({ ...s, busy: false, error: true }));
    }
  };

  if (!store?.id) return null;

  /* ── κεφαλίδα ── */
  let periodText: string;
  switch (sel.key) {
    case 'today': periodText = fmtLongDate(todayYmd, lang); break;
    case 'yesterday': periodText = fmtLongDate(dates.to, lang); break;
    case '7d': periodText = t('home:subtitle.last7', { range: rangeText(dates.from, dates.to, lang) }); break;
    case '30d': periodText = t('home:subtitle.last30', { range: rangeText(dates.from, dates.to, lang) }); break;
    default: periodText = rangeText(dates.from, dates.to, lang);
  }
  const openSince = openSinceText(home.attention?.register.open_since ?? null, todayYmd, lang);
  const subtitle = sel.key === 'today' && openSince ? `${periodText} · ${t('home:subtitle.registerOpenSince', { time: openSince })}` : periodText;

  const tools = (
    <>
      <PeriodPicker sel={sel} onChange={changePeriod} onTodayTap={onTodayTap} />
      <button
        type="button"
        className="xh-btn"
        onClick={openExport}
        aria-disabled={!premium || exp.busy || undefined}
        title={premium ? undefined : t('analytics:storeAnalytics.premiumRequired')}
        data-testid="home-export"
        data-locked={premium ? undefined : 'true'}
      >
        {exp.busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
        {t('home:export.button')}
        {!premium && <Lock className="xh-lock" aria-label={t('home:reports.lockedHint')} />}
      </button>
    </>
  );

  const status = (
    <>
      {home.error && home.current && data && (
        <p className="xh-status" role="status" data-testid="home-refresh-failed">
          {t('home:load.refreshFailed', { time: home.updatedAt ? fmtTime(new Date(home.updatedAt).toISOString()) : '' })}{' '}
          <button type="button" className="xh-link" onClick={home.reload}>{t('home:load.retry')}</button>
        </p>
      )}
      {exp.error && <p className="xh-status" role="alert">{t('home:export.failed')}</p>}
    </>
  );

  const attention = (
    <AttentionStrip
      attention={home.attention}
      now={now}
      onCloseRegister={access.any ? () => setHubOpen(true) : onNavigate ? () => onNavigate('registerreports') : undefined}
      onOpenPrinters={onNavigate ? () => onNavigate('printers') : undefined}
      onOpenFiscal={onNavigate ? () => onNavigate('ecr') : undefined}
    />
  );

  const reportTitle = (id: ReportId): string => {
    if (id === 'inventory' && store.waste_enabled === true) return t('home:reports.items.inventoryWaste');
    return t(`home:reports.items.${id}`);
  };

  const modals = (
    <>
      <RegisterHubModal isOpen={hubOpen} onClose={() => { setHubOpen(false); home.reload(); }} />
      <ExportReportModal
        isOpen={exp.open}
        onClose={() => setExp(s => ({ ...s, open: false }))}
        storeId={store.id}
        storeName={store.store_name || store.name}
        dateFrom={dates.from}
        dateTo={dates.to}
        analytics={exp.analytics}
      />
    </>
  );

  if (page !== 'home') {
    return (
      <div ref={rootRef} className="xh-page" style={vizStyle} data-testid="home-page">
        <ReportPage
          id={page}
          title={page === 'products' ? t('home:productsPage.title') : reportTitle(page)}
          periodText={periodText}
          periodKey={sel.key}
          dates={dates}
          tools={tools}
          attention={attention}
          home={{ data, current: home.current, loading: home.loading, error: home.error, reload: home.reload }}
          premium={premium}
          onBack={backHome}
        />
        {modals}
      </div>
    );
  }

  /* ── σώμα ── */
  const reportIds: ReportId[] = [];
  if (store.dine_in === true) reportIds.push('tables');
  if (store.takeaway !== false) reportIds.push('takeaway');
  if (store.delivery !== false) reportIds.push('delivery');
  reportIds.push('customers', 'promotions', 'stateFees');
  if (store.warehouse !== false) reportIds.push('inventory');
  else if (store.waste_enabled === true) reportIds.push('waste');
  reportIds.push('vat', 'audit');
  if (training) reportIds.push('training');
  const reportLinks: ReportLink[] = reportIds.map(id => ({ id, label: reportTitle(id), locked: isReportLocked(id, premium) }));

  let body: React.ReactNode;
  if (!data && !home.error) {
    body = (
      <>
        <KpisSkeleton />
        <SectionsSkeleton />
      </>
    );
  } else if (home.error && (!data || !home.current)) {
    body = (
      <SheetBlock>
        <HomeLoadError
          onRetry={home.reload}
          forbidden={home.error instanceof HomeApiError && home.error.status === 403}
          message={home.error instanceof HomeApiError ? home.error.serverMessage : null}
        />
      </SheetBlock>
    );
  } else if (data) {
    const key = data.period.key;
    const yesterdayYmd = yesterdayOf(data, todayYmd);
    const stale = !home.current;
    body = (
      <div className={`xh-body${stale ? ' is-stale' : ''}`} aria-busy={stale && home.loading ? true : undefined} data-testid="home-body">
        <KpiStrip items={buildKpis(t, data, home.attention, { dineIn: store.dine_in === true, todayYmd, lang })} ariaLabel={t('home:kpi.title')} />
        {training && (
          <div className="xh-training" data-testid="training-strip">
            <GraduationCap aria-hidden="true" />
            <span>{rich(t('home:training.strip', { count: data.training.orders, n: fmtNum(data.training.orders), amount: fmtEur(data.training.amount) }))}</span>
            <button type="button" className="xh-link" onClick={() => openReport('training')} data-testid="training-details">
              {t('home:training.details')}<ChevronRight aria-hidden="true" />
            </button>
            <button type="button" className="xh-link xh-link-muted" onClick={() => setTraining(false)}>{t('home:training.hide')}</button>
          </div>
        )}
        {key === 'today' && data.today_so_far && (
          <TodaySection tsf={data.today_so_far} yesterdayYmd={yesterdayYmd} until={compareUntil(data) || data.today_so_far.until} />
        )}
        <DaysSection rows={data.by_day} periodKey={key} todayYmd={todayYmd} />
        <HoursSection byHour={data.by_hour} />
        <ChannelsSection channels={data.channels} />
        <PaymentsSection payments={data.payments} />
        <ProductsSection top={data.top_products} onAll={() => openReport('products')} locked={!premium} />
        <StaffSection
          data={data}
          periodKey={key}
          openSinceTime={openSince}
          onRegisterReports={onNavigate ? () => onNavigate('registerreports') : undefined}
        />
        <CancellationsSection data={data} periodKey={key} dineIn={store.dine_in === true} onAll={() => openReport('cancellations')} locked={!premium} />
      </div>
    );
  }

  return (
    <div ref={rootRef} className="xh-page" style={vizStyle} data-testid="home-page">
      {/* Κενός τίτλος: την κεφαλίδα (h1 + χειριστήρια) τη γράφει το HomeHeader· του φύλλου κρύβεται (home.css). */}
      <SettingsSheet title="">
        <HomeHeader crumb={t('settings:nav.groups.overview')} title={t('home:title')} subtitle={subtitle} tools={tools} status={status} />
        {attention}
        {body}
        <ReportsBlock items={reportLinks} onOpen={openReport} />
      </SettingsSheet>
      {modals}
    </div>
  );
};

export default HomePage;
